import { useEffect, useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import LoadingSpinner from '@/components/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Combobox, ComboboxContent, ComboboxEmpty, ComboboxInput, ComboboxItem, ComboboxList } from '@/components/ui/combobox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import useUnidades from '@/hooks/useUnidades';
import { createConversao, fetchConversoes, updateConversao } from '@/services/conversaoUnidadeService';
import { fetchProdutosERP, fetchProdutosPGA, produtoKeys } from '@/services/produtoService';
import { fetchUnidadesMedidaCadastradas, unidadeMedidaKeys } from '@/services/unidadeMedidaService';
import {
  createVinculo,
  deleteVinculo,
  fetchProdutosSemVinculo,
  fetchVinculos,
  updateVinculo,
  vinculoKeys,
} from '@/services/vinculoService';
import { parseApiError } from '@/utils/apiError';
import { mergeUnidadesMedida, normalizeUnidadeMedidaCodigo } from '@/utils/unidadesMedida';
import { vinculoFormSchema, type TVinculoFormData } from './schema';
import type { IUnidade } from '@/types/unidade';
import type { IProdutoERP, IProdutoPGA } from '@/types/produto';
import type { IVinculo, IVinculoPayload } from '@/types/vinculo';

export const Route = createFileRoute('/_authenticated/vinculos-mapa/')({
  component: VinculosMapa,
});

type TTab = 'vinculos' | 'sem-vinculo' | 'erp' | 'pga';

interface IUnidadeMedidaSelectOption {
  value: string;
  label: string;
}

const emptyFormValues: TVinculoFormData = {
  codigo_erp: '',
  descricao_erp: '',
  codigo_mapa: '',
  descricao_mapa: '',
  unidade_origem: '',
  unidade_destino: '',
  fator_conversao: '',
  unidade_id: '',
};

function VinculosMapa() {
  const queryClient = useQueryClient();

  const [selectedTab, setSelectedTab] = useState<TTab>('vinculos');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [showVinculoModal, setShowVinculoModal] = useState(false);
  const [editingVinculo, setEditingVinculo] = useState<IVinculo | null>(null);
  const [editingConversaoId, setEditingConversaoId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const [erpSearchTerm, setErpSearchTerm] = useState('');
  const [erpDebounced, setErpDebounced] = useState('');
  const [selectedErp, setSelectedErp] = useState<IProdutoERP | null>(null);

  const [pgaSearchTerm, setPgaSearchTerm] = useState('');
  const [pgaDebounced, setPgaDebounced] = useState('');
  const [selectedPga, setSelectedPga] = useState<IProdutoPGA | null>(null);
  const [comboboxPortalContainer, setComboboxPortalContainer] = useState<HTMLElement | null>(null);

  const vinculoForm = useForm<TVinculoFormData>({
    resolver: zodResolver(vinculoFormSchema),
    mode: 'onChange',
    defaultValues: emptyFormValues,
  });
  const {
    control: vinculoControl,
    handleSubmit: handleVinculoSubmit,
    reset: resetVinculoForm,
    setValue: setVinculoValue,
    getValues: getVinculoValues,
    formState: { errors: vinculoErrors, isValid: isVinculoValid },
  } = vinculoForm;

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const timer = setTimeout(() => setErpDebounced(erpSearchTerm), 300);
    return () => clearTimeout(timer);
  }, [erpSearchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => setPgaDebounced(pgaSearchTerm), 300);
    return () => clearTimeout(timer);
  }, [pgaSearchTerm]);

  const { data: unidades, loading: unidadesLoading, error: unidadesError } = useUnidades({
    skip: 0,
    limit: 100,
    enabled: showVinculoModal,
  });

  const singleUnidadeSelectLocked = unidades.length === 1 && !unidadesLoading && !unidadesError;

  useEffect(() => {
    if (!showVinculoModal || !singleUnidadeSelectLocked) return;
    const onlyId = String(unidades[0].id);
    if (getVinculoValues('unidade_id') !== onlyId) {
      setVinculoValue('unidade_id', onlyId, { shouldValidate: true });
    }
  }, [showVinculoModal, singleUnidadeSelectLocked, unidades, getVinculoValues, setVinculoValue]);

  const { data: unidadesMedidaData } = useQuery({
    queryKey: unidadeMedidaKeys.cadastradas,
    queryFn: fetchUnidadesMedidaCadastradas,
    enabled: showVinculoModal,
  });
  const unidadesCadastradas = unidadesMedidaData?.items ?? [];

  const unidadeMedidaOptions = useMemo(
    () => mergeUnidadesMedida(unidadesCadastradas).map((u) => ({ value: u.value, label: `${u.label} (${u.value})` })),
    [unidadesCadastradas],
  );

  const listParams = { page: currentPage, page_size: pageSize, search: searchTerm };

  const vinculosQuery = useQuery({
    queryKey: vinculoKeys.list(listParams),
    queryFn: () => fetchVinculos(listParams),
    enabled: selectedTab === 'vinculos',
  });
  const erpQuery = useQuery({
    queryKey: produtoKeys.erp(listParams),
    queryFn: () => fetchProdutosERP(listParams),
    enabled: selectedTab === 'erp',
  });
  const pgaQuery = useQuery({
    queryKey: produtoKeys.pga(listParams),
    queryFn: () => fetchProdutosPGA(listParams),
    enabled: selectedTab === 'pga',
  });
  const semVinculoQuery = useQuery({
    queryKey: vinculoKeys.semVinculo(listParams),
    queryFn: () => fetchProdutosSemVinculo(listParams),
    enabled: selectedTab === 'sem-vinculo',
  });

  const activeQuery = { vinculos: vinculosQuery, erp: erpQuery, pga: pgaQuery, 'sem-vinculo': semVinculoQuery }[selectedTab];
  const loading = activeQuery.isLoading;
  const vinculos = vinculosQuery.data?.items ?? [];
  const erpProdutos = erpQuery.data?.items ?? [];
  const pgaProdutos = pgaQuery.data?.items ?? [];
  const semVinculoProdutos = semVinculoQuery.data?.items ?? [];
  const totalPages = activeQuery.data?.total_pages ?? 1;
  const totalCount = activeQuery.data?.total_count ?? 0;

  const erpDropdownParams = { page: 1, page_size: 10, search: erpDebounced };
  const erpDropdownQuery = useQuery({
    queryKey: produtoKeys.erp(erpDropdownParams),
    queryFn: () => fetchProdutosERP(erpDropdownParams),
    enabled: erpDebounced.length >= 2,
  });
  const erpResults = erpDropdownQuery.data?.items ?? [];
  const loadingErp = erpDropdownQuery.isFetching;

  const pgaDropdownParams = { page: 1, page_size: 10, search: pgaDebounced };
  const pgaDropdownQuery = useQuery({
    queryKey: produtoKeys.pga(pgaDropdownParams),
    queryFn: () => fetchProdutosPGA(pgaDropdownParams),
    enabled: pgaDebounced.length >= 2,
  });
  const pgaResults = pgaDropdownQuery.data?.items ?? [];
  const loadingPga = pgaDropdownQuery.isFetching;

  const createVinculoMutation = useMutation({ mutationFn: createVinculo });
  const updateVinculoMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: IVinculoPayload }) => updateVinculo(id, payload),
  });
  const deleteVinculoMutation = useMutation({ mutationFn: deleteVinculo });
  const isSavingVinculo = createVinculoMutation.isPending || updateVinculoMutation.isPending;
  const descricaoErpValue = useWatch({ control: vinculoControl, name: 'descricao_erp' });

  const fetchConversaoByProduto = async (codigoProduto: string) => {
    if (!codigoProduto) return;
    try {
      const data = await fetchConversoes({ search: codigoProduto, page: 1, page_size: 10 });
      const conversao = data.items.find((item) => item.codigo_produto === codigoProduto);
      if (!conversao) return;

      setEditingConversaoId(conversao.id);
      setVinculoValue('unidade_origem', conversao.unidade_origem || '');
      setVinculoValue('unidade_destino', conversao.unidade_destino || '');
      setVinculoValue('fator_conversao', String(conversao.fator_conversao ?? ''));
    } catch (err) {
      console.error('Erro ao buscar conversão do produto:', err);
    }
  };

  const saveConversaoUnidade = async (formData: TVinculoFormData) => {
    const { unidade_origem, unidade_destino, fator_conversao, codigo_erp, descricao_erp } = formData;
    if (!unidade_origem || !unidade_destino || !fator_conversao) return;

    const payload = {
      codigo_produto: codigo_erp,
      descricao_produto: descricao_erp || codigo_erp,
      unidade_origem,
      unidade_destino,
      fator_conversao: parseFloat(fator_conversao),
      unidade: formData.unidade_id,
    };

    try {
      if (editingConversaoId) {
        await updateConversao(editingConversaoId, {
          codigo_produto: payload.codigo_produto,
          descricao_produto: payload.descricao_produto,
          unidade_origem: payload.unidade_origem,
          unidade_destino: payload.unidade_destino,
          fator_conversao: payload.fator_conversao,
        });
      } else {
        await createConversao(payload);
      }
    } catch (err) {
      const wrapped = new Error(parseApiError(err, 'Erro ao salvar conversão de unidade')) as Error & { cause?: unknown };
      wrapped.cause = err;
      throw wrapped;
    }
  };

  const buildVinculoPayload = (formData: TVinculoFormData): IVinculoPayload => ({
    codigo_erp: formData.codigo_erp,
    descricao_erp: formData.descricao_erp,
    codigo_mapa: formData.codigo_mapa,
    descricao_mapa: formData.descricao_mapa,
    unidade_id: formData.unidade_id,
  });

  const refreshAfterVinculoSave = async () => {
    if (selectedTab === 'sem-vinculo') {
      await queryClient.invalidateQueries({ queryKey: vinculoKeys.semVinculo(listParams) });
    } else {
      await queryClient.invalidateQueries({ queryKey: vinculoKeys.all });
    }
  };

  const openCreateModal = () => {
    setEditingVinculo(null);
    setEditingConversaoId(null);
    resetVinculoForm(emptyFormValues);
    setErpSearchTerm('');
    setPgaSearchTerm('');
    setSelectedErp(null);
    setSelectedPga(null);
    setShowVinculoModal(true);
  };

  const closeVinculoModal = () => {
    setEditingVinculo(null);
    setEditingConversaoId(null);
    setShowVinculoModal(false);
    resetVinculoForm(emptyFormValues);
    setErpSearchTerm('');
    setPgaSearchTerm('');
    setSelectedErp(null);
    setSelectedPga(null);
  };

  const handleEditClick = async (vinculo: IVinculo) => {
    setEditingVinculo(vinculo);
    setEditingConversaoId(null);
    resetVinculoForm({
      ...emptyFormValues,
      codigo_erp: vinculo.codigo_erp,
      descricao_erp: vinculo.descricao_erp,
      codigo_mapa: vinculo.codigo_mapa,
      descricao_mapa: vinculo.descricao_mapa,
      unidade_id: String(vinculo.unidade_id),
    });
    setErpSearchTerm(vinculo.codigo_erp);
    setPgaSearchTerm(vinculo.codigo_mapa);
    setSelectedErp({
      produto_id: `edit-erp-${vinculo.codigo_erp}`,
      sku: vinculo.codigo_erp,
      descricao: vinculo.descricao_erp,
      unidade_id: String(vinculo.unidade_id),
    });
    setSelectedPga({
      cod_produto: vinculo.codigo_mapa,
      descricao: vinculo.descricao_mapa,
    });
    setShowVinculoModal(true);
    await fetchConversaoByProduto(vinculo.codigo_erp);
  };

  const handleCreateFromSemVinculo = (produto: IProdutoERP) => {
    setEditingVinculo(null);
    setEditingConversaoId(null);
    const origem = normalizeUnidadeMedidaCodigo(produto.unidade_medida);
    resetVinculoForm({
      ...emptyFormValues,
      codigo_erp: produto.sku || '',
      descricao_erp: produto.descricao || produto.nome || '',
      unidade_origem: origem,
      unidade_id: produto.unidade_id ? String(produto.unidade_id) : '',
    });
    setErpSearchTerm(produto.sku || '');
    setPgaSearchTerm('');
    setSelectedErp(produto);
    setSelectedPga(null);
    setShowVinculoModal(true);
  };

  const handleErpSelect = (produto: IProdutoERP | null) => {
    setSelectedErp(produto);
    if (!produto) {
      setVinculoValue('codigo_erp', '', { shouldValidate: true });
      setVinculoValue('descricao_erp', '');
      return;
    }
    setVinculoValue('codigo_erp', produto.sku || '', { shouldValidate: true });
    setVinculoValue('descricao_erp', produto.descricao || '');
    const origem = normalizeUnidadeMedidaCodigo(produto.unidade_medida);
    if (origem) setVinculoValue('unidade_origem', origem);
    setErpSearchTerm(produto.sku || '');
  };

  const handlePgaSelect = (produto: IProdutoPGA | null) => {
    setSelectedPga(produto);
    if (!produto) {
      setVinculoValue('codigo_mapa', '', { shouldValidate: true });
      setVinculoValue('descricao_mapa', '');
      return;
    }
    setVinculoValue('codigo_mapa', produto.cod_produto, { shouldValidate: true });
    setVinculoValue('descricao_mapa', produto.descricao || '');
    const destino = normalizeUnidadeMedidaCodigo(produto.unidade_medida);
    if (editingVinculo && destino) setVinculoValue('unidade_destino', destino);
    setPgaSearchTerm(produto.cod_produto);
  };

  const onSubmitVinculo = async (formData: TVinculoFormData) => {
    try {
      if (editingVinculo) {
        await updateVinculoMutation.mutateAsync({ id: editingVinculo.id, payload: buildVinculoPayload(formData) });
      } else {
        await createVinculoMutation.mutateAsync(buildVinculoPayload(formData));
      }

      if (formData.unidade_origem && formData.unidade_destino && formData.fator_conversao) {
        await saveConversaoUnidade(formData);
      }

      toast.success(editingVinculo ? 'Vínculo atualizado com sucesso.' : 'Vínculo criado com sucesso.');
      closeVinculoModal();
      await refreshAfterVinculoSave();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : parseApiError(err, editingVinculo ? 'Erro ao editar vínculo' : 'Erro ao criar vínculo'));
    }
  };

  const handleDeleteVinculo = async (vinculoId: string) => {
    try {
      await deleteVinculoMutation.mutateAsync(vinculoId);
      toast.success('Vínculo excluído com sucesso.');
    } catch (err) {
      toast.error(parseApiError(err, 'Erro ao excluir vínculo'));
    } finally {
      setDeleteConfirmId(null);
      await queryClient.invalidateQueries({ queryKey: vinculoKeys.all });
    }
  };

  const tabLabels: Record<TTab, string> = {
    vinculos: 'Vínculos',
    'sem-vinculo': 'Sem vínculo',
    erp: 'Produtos ERP',
    pga: 'Códigos PGA',
  };

  const isEmpty =
    (selectedTab === 'vinculos' && vinculos.length === 0) ||
    (selectedTab === 'erp' && erpProdutos.length === 0) ||
    (selectedTab === 'pga' && pgaProdutos.length === 0) ||
    (selectedTab === 'sem-vinculo' && semVinculoProdutos.length === 0);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold text-on-surface">Vínculos MAPA</h1>
        <p className="text-on-surface-variant">Gerencie os vínculos entre produtos e códigos MAPA</p>
      </div>

      <div className="rounded-lg border border-outline-variant p-4">
        <Tabs
          value={selectedTab}
          onValueChange={(value) => {
            setSelectedTab(value as TTab);
            setCurrentPage(1);
          }}
          className="mb-4"
        >
          <TabsList>
            {(Object.keys(tabLabels) as TTab[]).map((tab) => (
              <TabsTrigger
                key={tab}
                value={tab}
              >
                {tabLabels[tab]}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            setCurrentPage(1);
          }}
          className="flex items-end gap-4"
        >
          <Field className="flex-1">
            <FieldLabel htmlFor="search">Buscar {tabLabels[selectedTab]}</FieldLabel>
            <Input
              type="text"
              id="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Digite para buscar…"
              autoComplete="off"
            />
          </Field>
          <Button type="submit">Buscar</Button>
        </form>
      </div>

      <div className="rounded-lg border border-outline-variant">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-outline-variant p-4">
          <h2 className="text-lg font-semibold text-on-surface">
            {selectedTab === 'vinculos'
              ? 'Lista de Vínculos'
              : selectedTab === 'erp'
                ? 'Lista de Produtos ERP'
                : selectedTab === 'pga'
                  ? 'Lista de Códigos PGA'
                  : 'Produtos sem vínculo MAPA'}
          </h2>
          <div className="flex items-center gap-3">
            {selectedTab === 'vinculos' && <Button onClick={openCreateModal}>Criar Vínculo</Button>}
            <Field orientation="horizontal">
              <FieldLabel htmlFor="pageSize">Itens por página:</FieldLabel>
              <Select
                value={String(pageSize)}
                onValueChange={(value) => {
                  setPageSize(Number(value));
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger id="pageSize">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </Field>
          </div>
        </div>

        <div className="p-4">
          {loading && (
            <LoadingSpinner
              text="Carregando…"
              size="small"
            />
          )}

          {!loading && (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {selectedTab === 'vinculos' && (
                        <>
                          <TableHead>Código ERP</TableHead>
                          <TableHead>Descrição ERP</TableHead>
                          <TableHead>Código MAPA</TableHead>
                          <TableHead>Descrição MAPA</TableHead>
                          <TableHead>Data Criação</TableHead>
                          <TableHead>Unidade</TableHead>
                          <TableHead>Ações</TableHead>
                        </>
                      )}
                      {selectedTab === 'erp' && (
                        <>
                          <TableHead>SKU</TableHead>
                          <TableHead>Descrição</TableHead>
                        </>
                      )}
                      {selectedTab === 'pga' && (
                        <>
                          <TableHead>Código PGA</TableHead>
                          <TableHead>Descrição</TableHead>
                          <TableHead>Categoria</TableHead>
                          <TableHead>Área</TableHead>
                          <TableHead>Unidade</TableHead>
                        </>
                      )}
                      {selectedTab === 'sem-vinculo' && (
                        <>
                          <TableHead>SKU</TableHead>
                          <TableHead>Nome</TableHead>
                          <TableHead>Descrição</TableHead>
                          <TableHead>Unidade medida</TableHead>
                          <TableHead>Unidade</TableHead>
                          <TableHead>Ações</TableHead>
                        </>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedTab === 'vinculos' &&
                      vinculos.map((vinculo) => (
                        <TableRow key={vinculo.id}>
                          <TableCell>
                            <span className="rounded-full bg-primary px-2.5 py-1 text-xs font-medium text-on-primary">{vinculo.codigo_erp}</span>
                          </TableCell>
                          <TableCell>
                            <strong>{vinculo.descricao_erp}</strong>
                          </TableCell>
                          <TableCell>
                            <span className="rounded-full bg-secondary px-2.5 py-1 text-xs font-medium text-on-secondary">{vinculo.codigo_mapa}</span>
                          </TableCell>
                          <TableCell>
                            <strong>{vinculo.descricao_mapa}</strong>
                          </TableCell>
                          <TableCell>{new Date(vinculo.created_at).toLocaleDateString('pt-BR')}</TableCell>
                          <TableCell>{vinculo.unidade_nome}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => void handleEditClick(vinculo)}
                              >
                                Editar
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => setDeleteConfirmId(vinculo.id)}
                              >
                                Remover
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    {selectedTab === 'erp' &&
                      erpProdutos.map((produto) => (
                        <TableRow key={`${produto.sku}-${produto.unidade_id ?? ''}`}>
                          <TableCell>
                            <strong>{produto.sku}</strong>
                          </TableCell>
                          <TableCell>{produto.descricao}</TableCell>
                        </TableRow>
                      ))}
                    {selectedTab === 'pga' &&
                      pgaProdutos.map((produto, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <span className="rounded-full bg-primary px-2.5 py-1 text-xs font-medium text-on-primary">{produto.cod_produto}</span>
                          </TableCell>
                          <TableCell>
                            <strong>{produto.descricao}</strong>
                          </TableCell>
                          <TableCell>
                            <span className="rounded-full bg-success px-2.5 py-1 text-xs font-medium text-on-primary">{produto.categoria || 'N/A'}</span>
                          </TableCell>
                          <TableCell>
                            <span className="rounded-full bg-warning px-2.5 py-1 text-xs font-medium text-on-primary">{produto.area || 'N/A'}</span>
                          </TableCell>
                          <TableCell>
                            <span className="rounded-full bg-secondary px-2.5 py-1 text-xs font-medium text-on-secondary">{produto.unidade_medida || 'N/A'}</span>
                          </TableCell>
                        </TableRow>
                      ))}
                    {selectedTab === 'sem-vinculo' &&
                      semVinculoProdutos.map((produto) => (
                        <TableRow key={`${produto.sku}-${produto.unidade_id ?? produto.produto_id}`}>
                          <TableCell>
                            <strong>{produto.sku}</strong>
                          </TableCell>
                          <TableCell>{produto.nome || '—'}</TableCell>
                          <TableCell>{produto.descricao || '—'}</TableCell>
                          <TableCell>
                            <span className="rounded-full bg-secondary px-2.5 py-1 text-xs font-medium text-on-secondary">{produto.unidade_medida || 'N/A'}</span>
                          </TableCell>
                          <TableCell>{produto.unidade_nome || '—'}</TableCell>
                          <TableCell>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleCreateFromSemVinculo(produto)}
                            >
                              Criar vínculo
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>

              {isEmpty && (
                <div className="p-8 text-center">
                  <p className="text-lg text-on-surface-variant">
                    Nenhum{' '}
                    {selectedTab === 'vinculos'
                      ? 'vínculo'
                      : selectedTab === 'erp'
                        ? 'produto ERP'
                        : selectedTab === 'pga'
                          ? 'código PGA'
                          : 'produto sem vínculo'}{' '}
                    encontrado
                  </p>
                  <p className="text-on-surface-variant">Tente ajustar os filtros de busca</p>
                </div>
              )}

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-4 border-t border-outline-variant pt-4">
                  <span className="text-sm text-on-surface-variant">
                    Página {currentPage} de {totalPages} ({totalCount} registros)
                  </span>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Anterior
                    </Button>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let page: number;
                      if (currentPage <= 3) page = i + 1;
                      else if (currentPage > totalPages - 2) page = totalPages - 4 + i;
                      else page = currentPage - 2 + i;

                      if (page > 0 && page <= totalPages) {
                        return (
                          <Button
                            key={page}
                            variant={currentPage === page ? 'default' : 'outline'}
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </Button>
                        );
                      }
                      return null;
                    })}
                    <Button
                      variant="outline"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Próxima
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Dialog
        open={showVinculoModal}
        onOpenChange={(open) => {
          if (!open) closeVinculoModal();
        }}
        modal
      >
        <DialogContent
          ref={setComboboxPortalContainer}
          className="sm:max-w-2xl"
        >
          <DialogHeader>
            <DialogTitle>{editingVinculo ? 'Editar Vínculo' : 'Criar Vínculo'}</DialogTitle>
          </DialogHeader>

          <form onSubmit={(e) => void handleVinculoSubmit(onSubmitVinculo)(e)}>
            <FieldGroup>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field data-invalid={!!vinculoErrors.codigo_erp}>
                  <FieldLabel htmlFor="erpSearch">Produto ERP *</FieldLabel>
                  <Combobox
                    items={erpResults}
                    filter={null}
                    inputValue={erpSearchTerm}
                    onInputValueChange={setErpSearchTerm}
                    value={selectedErp}
                    onValueChange={handleErpSelect}
                    itemToStringLabel={(produto: IProdutoERP) => produto.sku ?? ''}
                    isItemEqualToValue={(a: IProdutoERP, b: IProdutoERP) => a.produto_id === b.produto_id || a.sku === b.sku}
                  >
                    <ComboboxInput
                      id="erpSearch"
                      placeholder="Digite para buscar produto ERP…"
                      autoComplete="off"
                      aria-invalid={!!vinculoErrors.codigo_erp}
                    />
                    <ComboboxContent container={comboboxPortalContainer}>
                      {loadingErp ? (
                        <div className="flex items-center justify-center gap-2 p-3 text-sm text-muted-foreground">
                          <Spinner /> Buscando…
                        </div>
                      ) : (
                        <>
                          <ComboboxEmpty>Nenhum produto encontrado</ComboboxEmpty>
                          <ComboboxList>
                            {(produto: IProdutoERP) => (
                              <ComboboxItem
                                key={produto.produto_id}
                                value={produto}
                              >
                                <div className="flex flex-col">
                                  <span className="font-medium">{produto.sku}</span>
                                  <span className="text-xs text-muted-foreground">{produto.descricao}</span>
                                </div>
                              </ComboboxItem>
                            )}
                          </ComboboxList>
                        </>
                      )}
                    </ComboboxContent>
                  </Combobox>
                  {descricaoErpValue && <span className="text-sm text-on-surface-variant">Selecionado: {descricaoErpValue}</span>}
                  <FieldError errors={vinculoErrors.codigo_erp ? [vinculoErrors.codigo_erp] : undefined} />
                </Field>

                <Field data-invalid={!!vinculoErrors.codigo_mapa}>
                  <FieldLabel htmlFor="pgaSearch">Código PGA *</FieldLabel>
                  <Combobox
                    items={pgaResults}
                    filter={null}
                    inputValue={pgaSearchTerm}
                    onInputValueChange={setPgaSearchTerm}
                    value={selectedPga}
                    onValueChange={handlePgaSelect}
                    itemToStringLabel={(produto: IProdutoPGA) => produto.cod_produto}
                    isItemEqualToValue={(a: IProdutoPGA, b: IProdutoPGA) => a.cod_produto === b.cod_produto}
                  >
                    <ComboboxInput
                      id="pgaSearch"
                      placeholder="Digite para buscar código PGA…"
                      autoComplete="off"
                      aria-invalid={!!vinculoErrors.codigo_mapa}
                    />
                    <ComboboxContent container={comboboxPortalContainer}>
                      {loadingPga ? (
                        <div className="flex items-center justify-center gap-2 p-3 text-sm text-muted-foreground">
                          <Spinner /> Buscando…
                        </div>
                      ) : (
                        <>
                          <ComboboxEmpty>Nenhum código encontrado</ComboboxEmpty>
                          <ComboboxList>
                            {(produto: IProdutoPGA) => (
                              <ComboboxItem
                                key={produto.cod_produto}
                                value={produto}
                              >
                                <div className="flex flex-col">
                                  <span className="font-medium">{produto.cod_produto}</span>
                                  <span className="text-xs text-muted-foreground">{produto.descricao}</span>
                                </div>
                              </ComboboxItem>
                            )}
                          </ComboboxList>
                        </>
                      )}
                    </ComboboxContent>
                  </Combobox>
                  <FieldError errors={vinculoErrors.codigo_mapa ? [vinculoErrors.codigo_mapa] : undefined} />
                </Field>

                <Field data-invalid={!!vinculoErrors.unidade_origem}>
                  <FieldLabel htmlFor="unidade_origem">Unidade de origem</FieldLabel>
                  <Controller
                    control={vinculoControl}
                    name="unidade_origem"
                    render={({ field }) => (
                      <Combobox
                        items={unidadeMedidaOptions}
                        itemToStringLabel={(option: IUnidadeMedidaSelectOption) => option.label}
                        value={unidadeMedidaOptions.find((option) => option.value === field.value) ?? null}
                        onValueChange={(option: IUnidadeMedidaSelectOption | null) => field.onChange(option?.value ?? '')}
                      >
                        <ComboboxInput
                          id="unidade_origem"
                          placeholder="Selecione a unidade de origem…"
                          autoComplete="off"
                        />
                        <ComboboxContent container={comboboxPortalContainer}>
                          <ComboboxEmpty>Nenhuma unidade encontrada</ComboboxEmpty>
                          <ComboboxList>
                            {(option: IUnidadeMedidaSelectOption) => (
                              <ComboboxItem
                                key={option.value}
                                value={option}
                              >
                                {option.label}
                              </ComboboxItem>
                            )}
                          </ComboboxList>
                        </ComboboxContent>
                      </Combobox>
                    )}
                  />
                  <FieldError errors={vinculoErrors.unidade_origem ? [vinculoErrors.unidade_origem] : undefined} />
                </Field>

                <Field data-invalid={!!vinculoErrors.unidade_destino}>
                  <FieldLabel htmlFor="unidade_destino">Unidade de destino</FieldLabel>
                  <Controller
                    control={vinculoControl}
                    name="unidade_destino"
                    render={({ field }) => (
                      <Combobox
                        items={unidadeMedidaOptions}
                        itemToStringLabel={(option: IUnidadeMedidaSelectOption) => option.label}
                        value={unidadeMedidaOptions.find((option) => option.value === field.value) ?? null}
                        onValueChange={(option: IUnidadeMedidaSelectOption | null) => field.onChange(option?.value ?? '')}
                      >
                        <ComboboxInput
                          id="unidade_destino"
                          placeholder="Selecione a unidade de destino…"
                          autoComplete="off"
                        />
                        <ComboboxContent container={comboboxPortalContainer}>
                          <ComboboxEmpty>Nenhuma unidade encontrada</ComboboxEmpty>
                          <ComboboxList>
                            {(option: IUnidadeMedidaSelectOption) => (
                              <ComboboxItem
                                key={option.value}
                                value={option}
                              >
                                {option.label}
                              </ComboboxItem>
                            )}
                          </ComboboxList>
                        </ComboboxContent>
                      </Combobox>
                    )}
                  />
                  <FieldError errors={vinculoErrors.unidade_destino ? [vinculoErrors.unidade_destino] : undefined} />
                </Field>

                <Field data-invalid={!!vinculoErrors.fator_conversao}>
                  <FieldLabel htmlFor="fator_conversao">Fator de conversão</FieldLabel>
                  <Controller
                    control={vinculoControl}
                    name="fator_conversao"
                    render={({ field }) => (
                      <Input
                        id="fator_conversao"
                        type="number"
                        inputMode="decimal"
                        placeholder="Ex: 1000 (1 KG = 1000 G)"
                        step="any"
                        min="0"
                        autoComplete="off"
                        aria-invalid={!!vinculoErrors.fator_conversao}
                        {...field}
                      />
                    )}
                  />
                  <FieldError errors={vinculoErrors.fator_conversao ? [vinculoErrors.fator_conversao] : undefined} />
                </Field>

                <Field data-invalid={!!vinculoErrors.unidade_id}>
                  <FieldLabel htmlFor="unidade_id">Unidade *</FieldLabel>
                  <Controller
                    control={vinculoControl}
                    name="unidade_id"
                    render={({ field }) => (
                      <Combobox
                        items={unidades}
                        itemToStringLabel={(unidade: IUnidade) => unidade.nome || 'Sem nome'}
                        value={unidades.find((unidade) => String(unidade.id) === field.value) ?? null}
                        onValueChange={(unidade: IUnidade | null) => field.onChange(unidade ? String(unidade.id) : '')}
                        disabled={singleUnidadeSelectLocked}
                      >
                        <ComboboxInput
                          id="unidade_id"
                          placeholder={unidadesLoading ? 'Carregando unidades…' : 'Selecione a unidade…'}
                          autoComplete="off"
                          aria-invalid={!!vinculoErrors.unidade_id}
                          disabled={unidadesLoading || singleUnidadeSelectLocked}
                        />
                        <ComboboxContent container={comboboxPortalContainer}>
                          <ComboboxEmpty>Nenhuma unidade encontrada</ComboboxEmpty>
                          <ComboboxList>
                            {(unidade: IUnidade) => (
                              <ComboboxItem
                                key={unidade.id}
                                value={unidade}
                              >
                                {unidade.nome || 'Sem nome'}
                              </ComboboxItem>
                            )}
                          </ComboboxList>
                        </ComboboxContent>
                      </Combobox>
                    )}
                  />
                  {unidadesError && <span className="text-sm text-destructive">Erro ao carregar unidades: {unidadesError}</span>}
                  <FieldError errors={vinculoErrors.unidade_id ? [vinculoErrors.unidade_id] : undefined} />
                </Field>
              </div>

              <div className="flex gap-3">
                <Button
                  type="submit"
                  disabled={!isVinculoValid || isSavingVinculo}
                >
                  {isSavingVinculo ? (
                    <>
                      <Spinner data-icon="inline-start" />
                      {editingVinculo ? 'Salvando…' : 'Criando…'}
                    </>
                  ) : editingVinculo ? (
                    'Salvar Alterações'
                  ) : (
                    'Criar Vínculo'
                  )}
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  onClick={closeVinculoModal}
                  disabled={isSavingVinculo}
                >
                  Cancelar
                </Button>
              </div>
            </FieldGroup>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteConfirmId != null}
        onOpenChange={(open) => {
          if (!open) setDeleteConfirmId(null);
        }}
        modal
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar exclusão</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-on-surface-variant">Tem certeza que deseja excluir este vínculo? Esta ação não pode ser desfeita.</p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmId(null)}
              disabled={deleteVinculoMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={deleteVinculoMutation.isPending}
              onClick={() => {
                if (deleteConfirmId != null) void handleDeleteVinculo(deleteConfirmId);
              }}
            >
              {deleteVinculoMutation.isPending ? (
                <>
                  <Spinner data-icon="inline-start" />
                  Excluindo…
                </>
              ) : (
                'Excluir'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
